import { getBackendUrl } from '../utils/api';
import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Music, Search, Loader2, Radio } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const SongInfo = () => {
  const { t } = useLanguage();
  const [artist, setArtist] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [songData, setSongData] = useState(null);

  const handleSearch = async () => {
    if (!artist.trim() || !title.trim()) {
      toast.error(t('fillArtistTitle'));
      return;
    }

    setLoading(true);
    setSongData(null);

    try {
      const response = await axios.post(`${API}/song-info`, {
        artist: artist.trim(),
        title: title.trim()
      });

      setSongData(response.data);
      toast.success(t('infoFound'));
    } catch (error) {
      console.error('Song info error:', error);
      toast.error(t('searchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto mt-8 shadow-lg border-2 border-[#8A4FDB]/30">
      <CardHeader className="bg-gradient-to-r from-[#F5F0FA] to-[#EDE5F5] dark:from-[#2D0C4B] dark:to-[#4A1D7A]">
        <CardTitle className="flex items-center gap-2 text-2xl text-[#4A1D7A] dark:text-white">
          <Music className="w-6 h-6" />
          🎵 {t('songInfoTitle')}
        </CardTitle>
        <CardDescription className="text-[#6B3FA0] dark:text-[#C4B5FD]">
          {t('songInfoDesc')}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-6">
        {/* Search Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="artist" className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('artistLabel')} (Artist)
            </Label>
            <Input
              id="artist"
              type="text"
              placeholder={t('exampleArtist')}
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              onKeyPress={handleKeyPress}
              onPaste={(e) => {
                // React synthetic event handler - allows default paste behavior
              }}
              data-allow-paste="true"
              className="mt-1"
              disabled={loading}
              autoComplete="off"
            />
          </div>
          
          <div>
            <Label htmlFor="title" className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('titleLabel')} (Title)
            </Label>
            <Input
              id="title"
              type="text"
              placeholder={t('exampleTitle')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={handleKeyPress}
              onPaste={(e) => {
                // React synthetic event handler - allows default paste behavior
              }}
              data-allow-paste="true"
              className="mt-1"
              disabled={loading}
              autoComplete="off"
            />
          </div>
        </div>

        {/* Tip Box */}
        <div className="bg-[#8A4FDB]/10 dark:bg-[#6B3FA0]/30 border border-[#8A4FDB]/30 dark:border-[#8A4FDB]/50 rounded-md p-3 mb-4">
          <p className="text-sm text-[#4A1D7A] dark:text-[#E9D5FF] flex items-center gap-2">
            <span className="text-base">ℹ️</span>
            <span><strong>Tip:</strong> {t('tipLatinChars')}</span>
          </p>
        </div>

        <Button 
          onClick={handleSearch} 
          disabled={loading || !artist.trim() || !title.trim()}
          className="w-full bg-gradient-to-r from-[#4A1D7A] to-[#6B3FA0] hover:from-[#5E2D9E] hover:to-[#8A4FDB] text-white"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('searching')}
            </>
          ) : (
            <>
              <Search className="mr-2 h-4 w-4" />
              {t('searchSongInfo')}
            </>
          )}
        </Button>

        {/* Results */}
        {songData && (
          <div className="mt-6 space-y-4">
            {/* Song Information with Album Cover */}
            <div className="bg-[#F5F0FA] dark:bg-[#2D0C4B]/80 rounded-lg p-4 border border-[#8A4FDB]/30">
              <h3 className="font-bold text-lg text-[#4A1D7A] dark:text-white mb-3 flex items-center gap-2">
                <Music className="w-5 h-5" />
                {songData.artist} - {songData.title}
              </h3>
              
              <div className="flex gap-4">
                {/* Album Cover Image */}
                {songData.album_image && (
                  <div className="flex-shrink-0">
                    <img 
                      src={songData.album_image} 
                      alt={`${songData.artist} - Album Cover`}
                      className="w-32 h-32 rounded-lg shadow-lg object-cover"
                    />
                  </div>
                )}
                
                {/* Song Info Text */}
                <div className="flex-1 prose prose-sm max-w-none text-[#57534E] dark:text-[#E9D5FF] whitespace-pre-line">
                  {songData.info}
                </div>
              </div>
            </div>

            {/* Radio Announcement Suggestion */}
            <div className="bg-gradient-to-r from-[#EDE5F5] to-[#F5F0FA] dark:from-[#4A1D7A] dark:to-[#2D0C4B] rounded-lg p-4 border-2 border-[#8A4FDB]/40">
              <h3 className="font-bold text-lg text-[#4A1D7A] dark:text-white mb-3 flex items-center gap-2">
                <Radio className="w-5 h-5" />
                💬 {t('radioAnnouncement')}
              </h3>
              
              <div className="bg-white dark:bg-[#1A0730] rounded-md p-4 text-[#3F3F46] dark:text-[#E9D5FF] italic border border-[#8A4FDB]/30">
                "{songData.radio_announcement}"
              </div>
              
              <p className="text-xs text-[#78716C] dark:text-[#A5B4FC] mt-2">
                💡 {t('announcementTip')}
              </p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!songData && !loading && (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500">
            <Music className="w-16 h-16 mx-auto mb-3 opacity-30" />
            <p>{t('fillArtistTitleSearch')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SongInfo;
